// ============================================================================
// KLINISOL CRO - Form Renderer Component
// Dynamic form generator from JSON schema
// Supports auto-population and validation
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    Save, 
    Send, 
    AlertCircle, 
    CheckCircle, 
    Loader2,
    ArrowLeft,
    Sparkles
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import formApi from '../services/formApi';

const FormRenderer = ({ mode = 'create' }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { schemaId, submissionId } = useParams();
    
    const [schema, setSchema] = useState(null);
    const [formData, setFormData] = useState({});
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [autoPopulatedFields, setAutoPopulatedFields] = useState([]);

    useEffect(() => {
        loadData();
    }, [schemaId, submissionId, mode]);

    const loadData = async () => {
        setLoading(true);
        setGeneralError('');
        
        try {
            if (mode === 'create') {
                // Load schema for new form
                const schemaData = await formApi.getSchemaById(schemaId);
                setSchema(schemaData);
                
                // Initialize empty form data
                const initialData = {};
                schemaData.schema_json.sections.forEach(section => {
                    section.fields.forEach(field => {
                        initialData[field.field_id] = '';
                    });
                });
                setFormData(initialData);
                
            } else {
                // Load existing submission
                const submissionData = await formApi.getSubmissionById(submissionId);
                setSubmission(submissionData);
                setSchema({
                    ...submissionData,
                    schema_json: submissionData.schema_json
                });
                setFormData(submissionData.submission_data);
            }
        } catch (err) {
            console.error('Error loading form:', err);
            setGeneralError('Failed to load form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (fieldId, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldId]: value
        }));
        
        // Clear error for this field
        if (errors[fieldId]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldId];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        schema.schema_json.sections.forEach(section => {
            section.fields.forEach(field => {
                if (field.required && !formData[field.field_id]) {
                    newErrors[field.field_id] = 'This field is required';
                }
                
                // Type-specific validation
                if (formData[field.field_id]) {
                    if (field.type === 'email' && !isValidEmail(formData[field.field_id])) {
                        newErrors[field.field_id] = 'Invalid email format';
                    }
                    
                    if (field.type === 'number' && isNaN(formData[field.field_id])) {
                        newErrors[field.field_id] = 'Must be a valid number';
                    }
                }
            });
        });
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSave = async () => {
        setSaving(true);
        setGeneralError('');
        
        try {
            if (mode === 'create') {
                const response = await formApi.createSubmission({
                    formSchemaId: schemaId,
                    submissionData: formData
                });
                
                setAutoPopulatedFields(response.auto_populated_fields || []);
                alert('Form saved as draft successfully!');
                navigate('/forms');
                
            } else {
                await formApi.updateSubmission(submissionId, {
                    submissionData: formData
                });
                
                alert('Form updated successfully!');
                navigate('/forms');
            }
        } catch (err) {
            console.error('Error saving form:', err);
            setGeneralError(err.response?.data?.message || 'Failed to save form. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setGeneralError('Please fix the errors before submitting.');
            return;
        }
        
        if (!window.confirm('Submit this form for approval? You will not be able to edit it after submission.')) {
            return;
        }
        
        setSubmitting(true);
        setGeneralError('');
        
        try {
            let currentSubmissionId = submissionId;
            
            // If creating new, save first
            if (mode === 'create') {
                const response = await formApi.createSubmission({
                    formSchemaId: schemaId,
                    submissionData: formData
                });
                currentSubmissionId = response.submission_id;
            } else {
                // Update existing
                await formApi.updateSubmission(submissionId, {
                    submissionData: formData
                });
            }
            
            // Submit for approval
            await formApi.submitForApproval(currentSubmissionId);
            
            alert('Form submitted successfully!');
            navigate('/forms');
            
        } catch (err) {
            console.error('Error submitting form:', err);
            setGeneralError(err.response?.data?.message || 'Failed to submit form. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderField = (field, sectionId) => {
        const fieldId = field.field_id;
        const value = formData[fieldId] || '';
        const error = errors[fieldId];
        const isAutoPopulated = autoPopulatedFields.includes(fieldId);
        
        const baseInputClass = `w-full px-4 py-3 border-2 rounded-xl text-base transition-all
            ${error 
                ? 'border-red-500 bg-red-50 focus:border-red-600' 
                : 'border-gray-200 bg-gray-50 focus:border-[#475569] focus:bg-white'
            }
            focus:outline-none focus:ring-4 focus:ring-[#475569]/10`;
        
        return (
            <div key={fieldId} className="mb-6">
                <label htmlFor={fieldId} className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                    {isAutoPopulated && (
                        <span className="flex items-center gap-1 text-xs font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                            <Sparkles className="w-3 h-3" />
                            Auto-filled
                        </span>
                    )}
                </label>
                
                {field.type === 'textarea' ? (
                    <textarea
                        id={fieldId}
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                        placeholder={field.placeholder || ''}
                        rows={4}
                        className={`${baseInputClass} resize-vertical`}
                        disabled={submission?.status !== 'draft' && mode === 'edit'}
                    />
                ) : field.type === 'select' ? (
                    <select
                        id={fieldId}
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                        className={baseInputClass}
                        disabled={submission?.status !== 'draft' && mode === 'edit'}
                    >
                        <option value="">Select...</option>
                        {field.options && field.options.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        id={fieldId}
                        type={field.type || 'text'}
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                        placeholder={field.placeholder || ''}
                        className={baseInputClass}
                        disabled={submission?.status !== 'draft' && mode === 'edit'}
                    />
                )}
                
                {field.help_text && (
                    <p className="mt-2 text-xs text-gray-600">{field.help_text}</p>
                )}
                
                {error && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </p>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f8fafd] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-[#475569] mx-auto mb-4" />
                    <p className="text-gray-600">Loading form...</p>
                </div>
            </div>
        );
    }

    if (!schema) {
        return (
            <div className="min-h-screen bg-[#f8fafd] flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <p className="text-gray-900 font-bold mb-2">Form not found</p>
                    <button
                        onClick={() => navigate('/forms')}
                        className="text-[#475569] hover:underline"
                    >
                        Return to Forms
                    </button>
                </div>
            </div>
        );
    }

    const canEdit = mode === 'create' || (submission && submission.status === 'draft');

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 shadow-md">
                <div className="max-w-4xl mx-auto">
                    <button
                        onClick={() => navigate('/forms')}
                        className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Forms
                    </button>
                    <h1 className="text-2xl font-bold">{schema.document_number}</h1>
                    <p className="text-sm opacity-90">{schema.title}</p>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-8">
                {/* General Error */}
                {generalError && (
                    <div className="bg-red-50 border-2 border-red-500 text-red-900 px-6 py-4 rounded-xl mb-6 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p>{generalError}</p>
                    </div>
                )}

                {/* Form */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    {schema.schema_json.sections.map(section => (
                        <div key={section.section_id} className="mb-8 last:mb-0">
                            <h2 className="text-xl font-bold text-[#475569] mb-6 pb-3 border-b-2 border-gray-200">
                                {section.section_title}
                            </h2>
                            {section.fields.map(field => renderField(field, section.section_id))}
                        </div>
                    ))}
                </div>

                {/* Actions */}
                {canEdit && (
                    <div className="flex gap-4 mt-6">
                        <button
                            onClick={handleSave}
                            disabled={saving || submitting}
                            className="flex-1 py-4 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-5 h-5" />
                                    Save Draft
                                </>
                            )}
                        </button>
                        
                        <button
                            onClick={handleSubmit}
                            disabled={saving || submitting}
                            className="flex-[2] py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Send className="w-5 h-5" />
                                    Submit for Approval
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormRenderer;
